import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Upload, FileText, Bell, Mail,
  CalendarClock, Store, CircleCheck, CircleX, Loader2, Copy,
} from "lucide-react";
import { api, apiError } from "../api";
import { Spinner, Toast, useToast } from "../components/ui";

const CATALOGUES = [
  { key: "retail", label: "Retail Prices", desc: "Maximum retail prices (P catalogue)" },
  { key: "subsidised", label: "Subsidised Prices", desc: "Subsidised maximum retail prices (S catalogue)" },
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MINUTE_OPTIONS = [0, 15, 30, 45];
const MAX_CSV_BYTES = 5 * 1024 * 1024;

export default function ProfileNew() {
  const navigate = useNavigate();
  const { toast, show, close } = useToast();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [catalogue, setCatalogue] = useState("retail");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const [csvRows, setCsvRows] = useState([]);
  const [csvInfo, setCsvInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importSource, setImportSource] = useState(null); // {label} when loaded from another profile

  const [sourceProfiles, setSourceProfiles] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [loadingSource, setLoadingSource] = useState(false);

  const [hour, setHour] = useState(8);
  const [minute, setMinute] = useState(0);
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]);

  const [notifyPush, setNotifyPush] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);

  const testSource = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/profiles/test-source", { catalogue });
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, message: apiError(e) });
    } finally {
      setTesting(false);
    }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_CSV_BYTES) {
      show("CSV exceeds the 5 MB limit.", "error");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/profiles/csv-preview", form);
      setCsvRows(data.valid);
      setCsvInfo({ valid: data.valid_count, invalid: data.invalid_count, invalidRows: data.invalid });
      setImportSource(null);
      setSourceId("");
    } catch (err) {
      show(apiError(err), "error");
    } finally {
      setUploading(false);
    }
    e.target.value = "";
  };

  useEffect(() => {
    api.get("/profiles")
      .then(({ data }) => setSourceProfiles((data.profiles || []).filter((p) => p.product_count > 0)))
      .catch(() => {});
  }, []);

  const reuseFrom = async (pid) => {
    setSourceId(pid);
    if (!pid) {
      setCsvRows([]); setCsvInfo(null); setImportSource(null);
      return;
    }
    setLoadingSource(true);
    try {
      const { data } = await api.get(`/profiles/${pid}/products`);
      const rows = (data.products || []).map((p) => ({ name: p.name, ean: p.ean }));
      const src = sourceProfiles.find((p) => p.id === pid);
      setCsvRows(rows);
      setCsvInfo({ valid: rows.length, invalid: 0, invalidRows: [] });
      setImportSource({ label: src?.name || "another profile" });
      show(`Loaded ${rows.length} products from ${src?.name || "profile"}.`);
    } catch (e) {
      show(apiError(e), "error");
      setSourceId("");
    } finally {
      setLoadingSource(false);
    }
  };

  const toggleDay = (d) =>
    setWeekdays((w) => ((w || []).includes(d) ? (w || []).filter((x) => x !== d) : [...(w || []), d].sort()));

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/profiles", {
        name,
        catalogue,
        scan_hour: hour,
        scan_minute: minute,
        weekdays,
        notify_push: notifyPush,
        notify_email: notifyEmail,
        products: csvRows.map((r) => ({ name: r.name, ean: r.ean })),
      });
      show("Profile created.");
      navigate(`/profiles/${data.profile.id}`);
    } catch (e) {
      show(apiError(e), "error");
      setSaving(false);
    }
  };

  const canNext =
    (step === 1 && name.trim() && testResult?.ok) ||
    step === 2 ||
    step === 3 ||
    step === 4;

  return (
    <div className="fade-in">
      <Toast toast={toast} onClose={close} />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted mb-4" data-testid="wizard-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-display text-2xl md:text-3xl text-ink mb-1">New monitoring profile</h1>
      <p className="text-sm text-muted mb-5">Step {step} of 4</p>

      {/* Progress */}
      <div className="flex gap-2 mb-6">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-teal" : "bg-stone-border"}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="card p-5 space-y-5" data-testid="wizard-step-1">
          <div>
            <label className="label" htmlFor="pname">Profile name</label>
            <input id="pname" className="input" value={name} placeholder="e.g. Household staples"
              onChange={(e) => { setName(e.target.value); }} data-testid="wizard-name" />
          </div>
          <div>
            <span className="label">Choose a catalogue</span>
            <div className="space-y-2">
              {CATALOGUES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => { setCatalogue(c.key); setTestResult(null); }}
                  className={`w-full flex items-start gap-3 p-3 rounded-card border text-left transition-colors ${
                    catalogue === c.key ? "border-teal bg-teal-light" : "border-stone-border hover:bg-stone-border/30"
                  }`}
                  data-testid={`catalogue-${c.key}`}
                >
                  <Store className={`w-5 h-5 mt-0.5 ${catalogue === c.key ? "text-teal" : "text-muted"}`} />
                  <span className="flex-1">
                    <span className="block font-medium text-ink">{c.label}</span>
                    <span className="block text-xs text-muted">{c.desc}</span>
                  </span>
                  {catalogue === c.key && <Check className="w-5 h-5 text-teal" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <button className="btn-ghost w-full" onClick={testSource} disabled={testing} data-testid="wizard-test-source">
              {testing ? <Spinner /> : <CircleCheck className="w-4 h-4" />} Test source
            </button>
            {testResult && (
              <div className={`mt-3 flex items-start gap-2 text-sm p-3 rounded-card ${testResult.ok ? "bg-emerald-light text-emerald" : "bg-rose-light text-rose"}`} data-testid="wizard-test-result">
                {testResult.ok ? <CircleCheck className="w-5 h-5 shrink-0" /> : <CircleX className="w-5 h-5 shrink-0" />}
                {testResult.message}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card p-5 space-y-4" data-testid="wizard-step-2">
          {sourceProfiles.length > 0 && (
            <div>
              <span className="label flex items-center gap-1.5"><Copy className="w-4 h-4 text-teal" /> Reuse a previous list</span>
              <p className="text-xs text-muted mb-2">Copy the products you already track in another profile.</p>
              <select
                className="input"
                value={sourceId}
                onChange={(e) => reuseFrom(e.target.value)}
                disabled={loadingSource}
                data-testid="wizard-reuse-select"
              >
                <option value="">Select a profile…</option>
                {sourceProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.product_count} product{p.product_count === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
              {loadingSource && <p className="text-xs text-muted mt-2 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 spin" /> Loading products…</p>}
              {importSource && !loadingSource && (
                <p className="text-xs text-teal mt-2" data-testid="wizard-reuse-loaded">Loaded from “{importSource.label}”. You can still upload a CSV to replace this.</p>
              )}
              <div className="flex items-center gap-3 my-4">
                <span className="h-px flex-1 bg-stone-border" />
                <span className="text-xs text-muted">or</span>
                <span className="h-px flex-1 bg-stone-border" />
              </div>
            </div>
          )}
          <div>
            <span className="label">Import products (optional)</span>
            <p className="text-xs text-muted mb-3">CSV with “EAN Code” and “Commodity Name” columns. Max 5 MB.</p>
            <label className="btn-ghost w-full cursor-pointer" data-testid="wizard-csv-label">
              {uploading ? <Spinner /> : <Upload className="w-4 h-4" />} Choose CSV file
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onCsv} data-testid="wizard-csv-input" />
            </label>
          </div>
          {csvInfo && (
            <div className="text-sm">
              <div className="flex gap-2 mb-3">
                <span className="chip bg-emerald-light text-emerald">{csvInfo.valid} valid</span>
                {csvInfo.invalid > 0 && <span className="chip bg-rose-light text-rose">{csvInfo.invalid} skipped</span>}
              </div>
              {csvRows.length > 0 && (
                <div className="border border-stone-border rounded-card divide-y divide-stone-border max-h-64 overflow-auto" data-testid="wizard-csv-preview">
                  {csvRows.slice(0, 50).map((r, i) => (
                    <div key={`${r.ean}-${r.name}-${i}`} className="flex items-center gap-2 p-2.5">
                      <FileText className="w-4 h-4 text-muted shrink-0" />
                      <span className="flex-1 truncate text-ink">{r.name}</span>
                      <span className="text-xs text-muted font-mono">{r.ean}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="card p-5 space-y-5" data-testid="wizard-step-3">
          <div className="flex items-center gap-2 text-ink">
            <CalendarClock className="w-5 h-5 text-teal" />
            <span className="font-medium">Automatic scan schedule</span>
          </div>
          <p className="text-xs text-muted -mt-3">Runs in Mauritius time (Indian/Mauritius).</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="hour">Hour</label>
              <select id="hour" className="input" value={hour} onChange={(e) => setHour(Number(e.target.value))} data-testid="wizard-hour">
                {Array.from({ length: 24 }).map((_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="minute">Minute</label>
              <select id="minute" className="input" value={minute} onChange={(e) => setMinute(Number(e.target.value))} data-testid="wizard-minute">
                {MINUTE_OPTIONS.map((m) => (
                  <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <span className="label">Days</span>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((d, i) => (
                <button key={d} onClick={() => toggleDay(i)}
                  className={`min-h-[44px] px-3 rounded-card border text-sm ${weekdays.includes(i) ? "bg-teal text-white border-teal" : "border-stone-border text-ink"}`}
                  data-testid={`wizard-day-${i}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="card p-5 space-y-3" data-testid="wizard-step-4">
          <span className="label">Notifications</span>
          <button onClick={() => setNotifyPush((v) => !v)}
            className={`w-full flex items-center gap-3 p-3 rounded-card border ${notifyPush ? "border-teal bg-teal-light" : "border-stone-border"}`}
            data-testid="wizard-notify-push">
            <Bell className={`w-5 h-5 ${notifyPush ? "text-teal" : "text-muted"}`} />
            <span className="flex-1 text-left font-medium text-ink">Browser push</span>
            {notifyPush && <Check className="w-5 h-5 text-teal" />}
          </button>
          <button onClick={() => setNotifyEmail((v) => !v)}
            className={`w-full flex items-center gap-3 p-3 rounded-card border ${notifyEmail ? "border-teal bg-teal-light" : "border-stone-border"}`}
            data-testid="wizard-notify-email">
            <Mail className={`w-5 h-5 ${notifyEmail ? "text-teal" : "text-muted"}`} />
            <span className="flex-1 text-left font-medium text-ink">Email</span>
            {notifyEmail && <Check className="w-5 h-5 text-teal" />}
          </button>
        </div>
      )}

      <div className="flex gap-2 mt-6">
        {step > 1 && (
          <button className="btn-ghost flex-1" onClick={() => setStep((s) => s - 1)} data-testid="wizard-prev">
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
        )}
        {step < 4 ? (
          <button className="btn-primary flex-1" onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="wizard-next">
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button className="btn-accent flex-1" onClick={create} disabled={saving} data-testid="wizard-create">
            {saving ? <Loader2 className="w-4 h-4 spin" /> : <Check className="w-4 h-4" />} Create profile
          </button>
        )}
      </div>
    </div>
  );
}
